import { ArrowBigUp } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useContentScrollFabBounds } from "@/hooks/useContentScrollFabBounds";

type Props = {
  /** CSS selector eller element for scroll-container. Default: [data-content-scroll="true"] */
  target?: string | HTMLElement | null;
  threshold?: number; // hvornår knappen vises (px)
  /** When set, FAB horizontal position aligns to this element instead of the scroll area. */
  boundsElement?: HTMLElement | null;
};

const DEFAULT_SELECTOR = '[data-content-scroll="true"]';

const ScrollToTopFab: React.FC<Props> = ({
  target = DEFAULT_SELECTOR,
  threshold = 200,
  boundsElement,
}) => {
  const [visible, setVisible] = useState(false);
  const bounds = useContentScrollFabBounds(boundsElement);

  useEffect(() => {
    const scroller =
      (typeof target === "string"
        ? document.querySelector<HTMLElement>(target)
        : target) || null;

    const getScrollTop = () => (scroller ? scroller.scrollTop : window.scrollY);

    const onScroll = () => setVisible(getScrollTop() > threshold);

    // lyt på den rigtige kilde
    (scroller || window).addEventListener("scroll", onScroll, {
      passive: true,
    });
    onScroll(); // init

    return () => (scroller || window).removeEventListener("scroll", onScroll);
  }, [target, threshold]);

  const scrollToTop = () => {
    const scroller =
      (typeof target === "string"
        ? document.querySelector<HTMLElement>(target)
        : target) || null;

    if (scroller) scroller.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!visible) return null;

  return (
    <button
      type="button"
      onClick={scrollToTop}
      aria-label="Scroll to top"
      className="fixed bottom-6 z-40 rounded-full shadow-lg bg-primary text-primary-foreground hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 w-11 h-11 flex items-center justify-center transition-[right] duration-200 ease-in-out"
      style={{ right: bounds ? `${bounds.rightInset}px` : "1.5rem" }}
    >
      <ArrowBigUp />
    </button>
  );
};

export default ScrollToTopFab;
